import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../common/Card/Card';
import Loader from '../common/Loader/Loader';
import Modal from '../common/Modal/Modal';
import Notifications from '../common/Notifications/Notifications';
import Settings from '../common/Settings/Settings';
import FormattedNumber from '../common/FormattedNumber/FormattedNumber';
import ExchangeConnectionModal from '../common/ExchangeConnectionModal/ExchangeConnectionModal';
import { useUser } from '../../contexts/UserContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useApi } from '../../hooks/useApi';
import { useTelegram } from '../../hooks/useTelegram';
import { useUserProfile } from '../../hooks/useUserProfile';
import { hapticFeedback, openLink } from '../../utils/telegram';
import { shouldUseFullscreen } from '../../utils/deviceUtils';
import toolsLogo from '../../assets/icons/tools-logo.svg';
import downloadLogo from '../../assets/icons/download-logo.svg';
import './Dashboard.css';
import SwipeToGetFoxButton from '../common/SwipeToGetFoxButton/SwipeToGetFoxButton';
import { useConfig } from '../../hooks/useConfig';

const Dashboard = () => {
  const { user, loading, refreshUserData } = useUser();
  const { t } = useLanguage();
  const { createDeposit, requestWithdrawal, getArbitrageHistory, convertFoxTokens, convertProfitToBalance, connectExchange } = useApi();
  const { user: telegramUser, isFullscreen } = useTelegram();
  const { displayName, photo } = useUserProfile();
  const navigate = useNavigate();
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showConvertFoxModal, setShowConvertFoxModal] = useState(false);
  const [showConvertProfitModal, setShowConvertProfitModal] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositCurrency, setDepositCurrency] = useState('USDT');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawCurrency, setWithdrawCurrency] = useState('USDT');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [foxAmount, setFoxAmount] = useState('');
  const [profitAmount, setProfitAmount] = useState('');
  const [isConverting, setIsConverting] = useState(false);
  const [convertError, setConvertError] = useState('');
  const [showExchangeConnectionModal, setShowExchangeConnectionModal] = useState(false);

  // State for touch handling for modal swipe-down
  const [modalStartY, setModalStartY] = useState(0);
  const [modalCurrentY, setModalCurrentY] = useState(0);
  const [modalIsDragging, setModalIsDragging] = useState(false);

  // State for arbitrage history
  const [arbitrageHistory, setArbitrageHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  // State for trade details modal
  const [showTradeDetailsModal, setShowTradeDetailsModal] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);

  const config = useConfig();

  // Log Telegram user data
  useEffect(() => {
    if (telegramUser) {
      console.log('Telegram user data:', telegramUser);
    }
  }, [telegramUser]);

  // Fetch arbitrage history when component mounts
  useEffect(() => {
    fetchArbitrageHistory();
  }, []);

  // Fetch arbitrage history from API
  const fetchArbitrageHistory = async () => {
    setHistoryLoading(true);
    setHistoryError(null);

    try {
      const historyData = await getArbitrageHistory();

      // Format the data for display
      const formattedHistory = historyData.map(item => ({
        id: item._id || item.id,
        date: new Date(item.executedAt || item.createdAt),
        pair: item.pair,
        sourceExchange: item.sourceExchange,
        targetExchange: item.targetExchange,
        sourcePrice: parseFloat(item.sourcePrice) || 0,
        targetPrice: parseFloat(item.targetPrice) || 0,

        commissionAmount: parseFloat(item.commissionAmount) || 0,
        commissionPercent: parseFloat(item.commissionPercent) || 0,

        profit: parseFloat(item.profit) || 0,
        profitPercent: parseFloat(item.profitPercent) || 0,
        amount: parseFloat(item.amount) || 0,
        status: item.status || 'completed'
      }));

      setArbitrageHistory(formattedHistory);
    } catch (error) {
      console.error('Error fetching arbitrage history:', error);
      setHistoryError(t('historyError'));
    } finally {
      setHistoryLoading(false);
    }
  };

  // Add this new useEffect to load notifications when component mounts
  useEffect(() => {
    if (!loading && user) {
      // Загружаем уведомления при загрузке пользовательских данных
      console.log('User data loaded, notifications should be initialized through context');
    }
  }, [loading, user]);

  if (loading) {
    return <Loader fullPage={true} />;
  }

  const handleDepositClick = () => {
    hapticFeedback.medium();
    setIsDepositModalOpen(true);
  };

  const handleWithdrawClick = () => {
    hapticFeedback.medium();
    setIsWithdrawModalOpen(true);
  };

  const handleDepositSubmit = async () => {
    if (!depositAmount || parseFloat(depositAmount) <= 0) {
      hapticFeedback.error();
      return;
    }

    try {
      setIsProcessing(true);
      hapticFeedback.medium();
      let a = await createDeposit(parseFloat(depositAmount), depositCurrency);

      hapticFeedback.success();
      setIsDepositModalOpen(false);

      // Открыть ссылку через Telegram WebApp API если доступно, иначе через window.open
      openLink(a.transaction.paymentUrl);
      // refreshUserData();
    } catch (error) {
      console.error('Deposit error:', error);
      hapticFeedback.error();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleWithdrawSubmit = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) < 100 || !withdrawAddress) {
      hapticFeedback.error();
      return;
    }

    // Проверяем, достаточно ли прибыли для вывода
    if (parseFloat(withdrawAmount) > (user?.totalProfit || 0)) {
      hapticFeedback.error();
      return;
    }

    try {
      setIsProcessing(true);
      hapticFeedback.medium();
      await requestWithdrawal(parseFloat(withdrawAmount), withdrawCurrency, withdrawAddress);
      hapticFeedback.success();
      setIsWithdrawModalOpen(false);
      refreshUserData();
    } catch (error) {
      console.error('Withdrawal error:', error);
      hapticFeedback.error();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleContactSupport = () => {
    hapticFeedback.light();
    // Here you would implement the logic to contact support
    console.log('Contact support clicked from Dashboard');
    window.open(config.supportLink, '_blank');
    // Could open a chat window, redirect to a support form, etc.
  };

  // Function to refresh arbitrage history
  const handleRefreshHistory = () => {
    hapticFeedback.light();
    fetchArbitrageHistory();
  };

  // Handle trade details click
  const handleTradeClick = (trade) => {
    hapticFeedback.medium();
    setSelectedTrade(trade);
    setShowTradeDetailsModal(true);
  };

  // Close trade details modal
  const closeTradeDetailsModal = () => {
    hapticFeedback.light();
    setShowTradeDetailsModal(false);
    setSelectedTrade(null);
  };

  // Handle exchange connection
  const handleConnectExchange = async (connectionData) => {
    try {
      console.log('Connecting to exchange:', connectionData);

      // Call API to connect exchange
      await connectExchange(connectionData);

      // Refresh user data to get updated exchange balance
      await refreshUserData();
    } catch (error) {
      console.error('Error connecting exchange:', error);
      throw error;
    }
  };

  const handleConvertFox = async () => {
    if (!foxAmount || parseFloat(foxAmount) < 500) {
      setConvertError(t('minimumConversionAmount'));
      return;
    }

    if (parseFloat(foxAmount) > (user?.tokenBalance || 0)) {
      setConvertError(t('insufficientFoxTokens'));
      return;
    }

    setIsConverting(true);
    setConvertError('');

    try {
      const result = await convertFoxTokens(foxAmount);
      hapticFeedback.success();

      // Refresh user data to get updated balances
      await refreshUserData();

      // Close modal and reset form
      setShowConvertFoxModal(false);
      setFoxAmount('');

      console.log('Fox conversion successful:', result);
    } catch (error) {
      console.error('Error converting Fox tokens:', error);
      hapticFeedback.error();

      if (error.response?.data?.message) {
        setConvertError(error.response.data.message);
      } else {
        setConvertError(t('conversionFailed'));
      }
    } finally {
      setIsConverting(false);
    }
  };

  // Swipe convert function
  const handleSwipeConvert = async () => {
    if (!foxAmount || parseFloat(foxAmount) < 500) {
      setConvertError(t('minimumConversionAmount'));
      return;
    }

    if (parseFloat(foxAmount) > (user?.tokenBalance || 0)) {
      setConvertError(t('insufficientFoxTokens'));
      return;
    }

    setIsConverting(true);
    setConvertError('');

    try {
      const result = await convertFoxTokens(foxAmount);
      hapticFeedback.success();

      // Refresh user data to get updated balances
      await refreshUserData();

      // Close modal and reset form after delay
      setTimeout(() => {
        setShowConvertFoxModal(false);
        setFoxAmount('');
      }, 2000);

      console.log('Fox conversion successful:', result);
    } catch (error) {
      console.error('Error converting Fox tokens:', error);
      hapticFeedback.error();

      if (error.response?.data?.message) {
        setConvertError(error.response.data.message);
      } else {
        setConvertError(t('conversionFailed'));
      }
    } finally {
      setIsConverting(false);
    }
  };

  // Profit conversion handlers
  const handleConvertProfit = async () => {
    if (!profitAmount || parseFloat(profitAmount) < 1) {
      setConvertError(t('minimumProfitConversionAmount'));
      return;
    }

    if (parseFloat(profitAmount) > (user?.totalProfit || 0)) {
      setConvertError(t('insufficientProfit'));
      return;
    }

    setIsConverting(true);
    setConvertError('');

    try {
      const result = await convertProfitToBalance(profitAmount);
      hapticFeedback.success();

      // Refresh user data to get updated balances
      await refreshUserData();

      // Close modal and reset form
      setShowConvertProfitModal(false);
      setProfitAmount('');

      console.log('Profit conversion successful:', result);
    } catch (error) {
      console.error('Error converting profit:', error);
      hapticFeedback.error();

      if (error.response?.data?.message) {
        setConvertError(error.response.data.message);
      } else {
        setConvertError(t('profitConversionFailed'));
      }
    } finally {
      setIsConverting(false);
    }
  };

  // Swipe profit convert function
  const handleSwipeProfitConvert = async () => {
    if (!profitAmount || parseFloat(profitAmount) < 1) {
      setConvertError(t('minimumProfitConversionAmount'));
      return;
    }

    if (parseFloat(profitAmount) > (user?.totalProfit || 0)) {
      setConvertError(t('insufficientProfit'));
      return;
    }

    setIsConverting(true);
    setConvertError('');

    try {
      const result = await convertProfitToBalance(profitAmount);
      hapticFeedback.success();

      // Refresh user data to get updated balances
      await refreshUserData();

      // Close modal and reset form after delay
      setTimeout(() => {
        setShowConvertProfitModal(false);
        setProfitAmount('');
      }, 2000);

      console.log('Profit conversion successful:', result);
    } catch (error) {
      console.error('Error converting profit:', error);
      hapticFeedback.error();

      if (error.response?.data?.message) {
        setConvertError(error.response.data.message);
      } else {
        setConvertError(t('profitConversionFailed'));
      }
    } finally {
      setIsConverting(false);
    }
  };

  // Modal swipe down handlers
  const handleModalTouchStart = (e) => {
    setModalStartY(e.touches[0].clientY);
    setModalCurrentY(e.touches[0].clientY);
    setModalIsDragging(true);
  };

  const handleModalTouchMove = (e) => {
    if (!modalIsDragging) return;
    setModalCurrentY(e.touches[0].clientY);
  };

  const handleModalTouchEnd = () => {
    if (!modalIsDragging) return;

    const deltaY = modalCurrentY - modalStartY;

    // If swipe down is more than 100px, close modal
    if (deltaY > 100) {
      setShowConvertFoxModal(false);
      hapticFeedback.light();
    }

    setModalIsDragging(false);
    setModalStartY(0);
    setModalCurrentY(0);
  };

  // Определяем нужно ли добавлять класс для полноэкранного режима
  const dashboardClasses = `dashboard ${shouldUseFullscreen() && isFullscreen ? 'fullscreen-mode' : ''}`.trim();

  return (
    <div className={dashboardClasses}>
      <div className="simple-card border-gradient">
        <div className="user-profile">
          <div className="user-avatar-container">
            <img src={photo} alt="User avatar" className="user-avatar" />
          </div>
          <div className="user-name">
            {displayName}
          </div>
          <div className="user-controls">
            <Notifications />
            <Settings onContactSupport={handleContactSupport} />
          </div>
        </div>

        <div className="balance-row">
          <div className="balance-label">{t('totalBalance')}</div>
          <FormattedNumber value={user?.balance} suffix="USDT" className="balance-value" />
        </div>
        {/* <div className="balance-row">
          <div className="balance-label">Депозит</div>
          <FormattedNumber value={user?.totalDeposit} suffix="USDT" className="balance-value" />
        </div> */}
        {/* <div className="balance-row">
          <div className="balance-label">Прибыль</div>
          <FormattedNumber value={user?.totalProfit} suffix="USDT" className="balance-value profit" />
        </div> */}

        <div className="balance-divider"></div>
        <div className="balance-row" style={{ marginBottom: '0px' }}>
          <div className="balance-label">{t('profit')}</div>
        </div>
        <div className="token-balance-row">
          <div className="token-balance"><FormattedNumber value={user?.totalProfit} suffix="USDT" className="balance-value profit" /></div>
          <button
            className={`exchange-button ${(user?.totalProfit || 0) < 1 ? 'disabled-obj' : ''}`}
            onClick={() => {
              if ((user?.totalProfit || 0) >= 1) {
                hapticFeedback.light();
                setShowConvertProfitModal(true);
              } else {
                hapticFeedback.error();
              }
            }}
            disabled={(user?.totalProfit || 0) < 1}
          >
            {t('convert')}
          </button>
        </div>


        <div className="balance-row" style={{ marginBottom: '0px' }}>
          <div className="balance-label">{t('foxBalance', { tokenName: config.tokenName || 'Token' })}</div>
        </div>
        <div className="token-balance-row">
          <div className="token-balance"><FormattedNumber value={user?.tokenBalance} suffix={config.tokenName || 'Token'} /></div>
          <button
            className={`exchange-button ${(user?.tokenBalance || 0) < 500 ? 'disabled-obj' : ''}`}
            onClick={() => {
              if ((user?.tokenBalance || 0) >= 500) {
                hapticFeedback.light();
                setShowConvertFoxModal(true);
              } else {
                hapticFeedback.error();
              }
            }}
            disabled={(user?.tokenBalance || 0) < 500}
          >
            {t('exchange')}
          </button>
        </div>

        <div className="balance-row" style={{ marginBottom: '0px' }}>
          <div className="balance-label">{t('exchangeBalance')}</div>
        </div>
        <div className="token-balance-row">
          {user?.exchangeBalance ? (
            <div className="token-balance">
              <FormattedNumber value={user.exchangeBalance} suffix="USDT" className="balance-value" />
            </div>
          ) : (
            <div className="token-balance">
              Не подключены
            </div>
          )}
        </div>

        {/* Exchange Management Link */}
        <div className="exchange-management-link">
          <button
            className="management-button"
            onClick={() => {
              hapticFeedback.light();
              navigate('/exchanges');
            }}
          >
            <img src={toolsLogo} alt="Manage" className="management-icon" />
            {t('exchangeManagement')}
          </button>
        </div>
      </div>

      <div className="action-buttons">
        <button className="action-button deposit" onClick={handleDepositClick}>
          <span className="button-icon">+</span> {t('deposit')}
        </button>
        <button className="action-button withdraw" onClick={handleWithdrawClick}>
          {t('withdraw')} <span className="button-icon">→</span>
        </button>
      </div>

      <div className="history-section">
        <div className="history-header">
          <div className="history-title">{t('arbitrageHistory')}</div>
          <div className="history-controls">
            <button className="history-control-button" onClick={() => {
              hapticFeedback.light();
              handleRefreshHistory();
            }}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={historyLoading ? 'spinning' : ''}
              >
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4.01 7.58 4.01 12C4.01 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z" fill="currentColor" />
              </svg>
            </button>
            {/* <button className="history-control-button" onClick={() => hapticFeedback.light()}>
              <img src={downloadLogo} alt="Download" />
            </button> */}
          </div>
        </div>

        <div className="history-items">
          {historyLoading ? (
            <div className="history-loading">
              <Loader size="small" />
              <div>{t('loadingHistory')}</div>
            </div>
          ) : historyError ? (
            <div className="history-error">
              <div>{historyError}</div>
              <button className="retry-button" onClick={() => {
                hapticFeedback.light();
                fetchArbitrageHistory();
              }}>
                {t('retry')}
              </button>
            </div>
          ) : arbitrageHistory.length === 0 ? (
            <div className="history-empty">
              <div>{t('noArbitrageOperations')}</div>
            </div>
          ) : (
            arbitrageHistory.map(item => (
              <div
                key={item.id}
                className="history-item"
                onClick={() => handleTradeClick(item)}
              >
                <div className="history-item-header">
                  <div className="history-date">{item.date.toLocaleDateString('ru-RU').replace(/\//g, '.')}</div>
                  <div className="history-time">{item.date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</div>
                </div>

                <div className="history-item-content">
                  <div className="history-item-row">
                    <div className="history-item-pair">{item.pair}</div>
                    <div className="history-item-exchanges">{item.sourceExchange} - {item.targetExchange}</div>
                  </div>

                  <div className="history-item-details">
                    <div className="history-detail">
                      <div className="history-detail-label">{t('profit')}</div>
                      <div className="history-detail-value"><FormattedNumber value={item.profit} suffix="USDT" /></div>
                    </div>
                    <div className="history-detail">
                      <div className="history-detail-label">{t('spread')}</div>
                      <div className="history-detail-value"><FormattedNumber value={item.profitPercent} suffix="%" /></div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <Modal
        isOpen={isDepositModalOpen}
        onClose={() => setIsDepositModalOpen(false)}
        title={t('depositModal')}
        actions={(
          <>
            <button
              className="button secondary"
              onClick={() => {
                hapticFeedback.light();
                setIsDepositModalOpen(false);
              }}
            >
              {t('cancel')}
            </button>
            <button
              className="button primary"
              onClick={() => {
                if (!isProcessing && depositAmount) {
                  hapticFeedback.medium();
                }
                handleDepositSubmit();
              }}
              disabled={isProcessing || !depositAmount}
            >
              {isProcessing ? t('processing') : t('deposit')}
            </button>
          </>
        )}
      >
        <div className="form-group">
          <label>{t('amount')}</label>
          <input
            type="number"
            value={depositAmount}
            onChange={(e) => setDepositAmount(e.target.value)}
            placeholder={t('enterAmount')}
            min="1"
            step="0.01"
          />
        </div>
        <div className="form-group">
          <label>{t('currency')}</label>
          <select
            value={depositCurrency}
            onChange={(e) => setDepositCurrency(e.target.value)}
          >
            <option value="USDT">USDT</option>
            <option value="BTC">BTC</option>
            <option value="ETH">ETH</option>
          </select>
        </div>
      </Modal>

      <Modal
        isOpen={isWithdrawModalOpen}
        onClose={() => setIsWithdrawModalOpen(false)}
        title={t('withdrawModal')}
        actions={(
          <>
            <button
              className="button secondary"
              onClick={() => {
                hapticFeedback.light();
                setIsWithdrawModalOpen(false);
              }}
            >
              {t('cancel')}
            </button>
            <button
              className="button primary"
              onClick={() => {
                if (!isProcessing && withdrawAmount && withdrawAddress) {
                  hapticFeedback.medium();
                }
                handleWithdrawSubmit();
              }}
              disabled={isProcessing || !withdrawAmount || !withdrawAddress || parseFloat(withdrawAmount) < 100 || parseFloat(withdrawAmount) > (user?.totalProfit || 0)}
            >
              {isProcessing ? t('processing') : t('withdraw')}
            </button>
          </>
        )}
      >
        <div className="form-group">
          <label>{t('amountToWithdraw')}</label>
          <input
            type="number"
            value={withdrawAmount}
            onChange={(e) => setWithdrawAmount(e.target.value)}
            placeholder={t('enterAmount')}
            min="100"
            step="0.01"
            max={user?.totalProfit || 0}
          />
          <div className="form-hint">
            {t('availableForWithdraw')}: <FormattedNumber value={user?.totalProfit || 0} suffix="USDT" /><br />
            {t('minimumWithdrawal')}: 100 USDT
          </div>
        </div>
        <div className="form-group">
          <label>{t('currency')}</label>
          <select
            value={withdrawCurrency}
            onChange={(e) => setWithdrawCurrency(e.target.value)}
          >
            <option value="USDT">USDT</option>
            <option value="BTC">BTC</option>
            <option value="ETH">ETH</option>
          </select>
        </div>
        <div className="form-group">
          <label>{t('walletAddress')}</label>
          <input
            type="text"
            value={withdrawAddress}
            onChange={(e) => setWithdrawAddress(e.target.value)}
            placeholder={t('enterAddress')}
          />
        </div>
      </Modal>

      {/* Fox Conversion Modal */}
      {showConvertFoxModal && (
        <>
          <div className="backdrop-overlay" onClick={() => setShowConvertFoxModal(false)}></div>
          <div
            className="fox-modal"
            onTouchStart={handleModalTouchStart}
            onTouchMove={handleModalTouchMove}
            onTouchEnd={handleModalTouchEnd}
            style={{
              transform: modalIsDragging && modalCurrentY > modalStartY
                ? `translateY(${Math.max(0, modalCurrentY - modalStartY)}px)`
                : 'translateY(0)'
            }}
          >
            <div className="fox-modal-header">
              <h2>{t('convertFoxTokens')}</h2>
              <button
                className="modal-close"
                onClick={() => setShowConvertFoxModal(false)}
              >
                ×
              </button>
            </div>

            <div className="fox-modal-body">
              <div className="conversion-info">
                <div className="conversion-rate">
                  <span>{t('conversionRate')}: 1 {config.tokenName || 'Token'} = 0.003 USDT</span>
                </div>
                <div className="available-balance">
                  <span>{t('availableFoxBalance', { tokenName: config.tokenName || 'Token' })}: <FormattedNumber value={user?.tokenBalance || 0} suffix={config.tokenName || 'Token'} /></span>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="foxAmount">{t('foxAmountToConvert')}</label>
                <input
                  id="foxAmount"
                  type="number"
                  placeholder={t('enterFoxAmount')}
                  value={foxAmount}
                  onChange={(e) => {
                    setFoxAmount(e.target.value);
                    setConvertError('');
                  }}
                  min="500"
                  max={user?.tokenBalance || 0}
                />
                <div className="minimum-hint">
                  {t('minimumConversion')}: 500 {config.tokenName || 'Token'}
                </div>
              </div>

              {foxAmount && parseFloat(foxAmount) >= 500 && (
                <div className="conversion-preview">
                  <div className="preview-row">
                    <span>{t('foxToConvert')}: {parseFloat(foxAmount).toFixed(0)} {config.tokenName || 'Token'}</span>
                  </div>
                  <div className="preview-row">
                    <span>{t('usdtToReceive')}: {(parseFloat(foxAmount) * 0.003).toFixed(3)} USDT</span>
                  </div>
                </div>
              )}

              {convertError && (
                <div className="error-message">{convertError}</div>
              )}
            </div>

            <div className="fox-modal-footer">
              <SwipeToGetFoxButton
                onGetFox={handleSwipeConvert}
                disabled={isConverting || !foxAmount || parseFloat(foxAmount) < 500 || parseFloat(foxAmount) > (user?.tokenBalance || 0)}
                customText={{
                  default: t('swipeToConvert'),
                  threshold: t('releaseToConvert'),
                  processing: t('converting'),
                  completed: `${t('converted')} ${foxAmount || 0} {config.tokenName || 'Token'}`
                }}
              />
            </div>
          </div>
        </>
      )}

      {/* Profit Conversion Modal */}
      {showConvertProfitModal && (
        <>
          <div className="backdrop-overlay" onClick={() => setShowConvertProfitModal(false)}></div>
          <div
            className="fox-modal"
            onTouchStart={handleModalTouchStart}
            onTouchMove={handleModalTouchMove}
            onTouchEnd={handleModalTouchEnd}
            style={{
              transform: modalIsDragging && modalCurrentY > modalStartY
                ? `translateY(${Math.max(0, modalCurrentY - modalStartY)}px)`
                : 'translateY(0)'
            }}
          >
            <div className="fox-modal-header">
              <h2>{t('convertProfitToBalance')}</h2>
              <button
                className="modal-close"
                onClick={() => setShowConvertProfitModal(false)}
              >
                ×
              </button>
            </div>

            <div className="fox-modal-body">
              <div className="conversion-info">
                <div className="conversion-rate">
                  <span>{t('conversionRate')}: 1 USDT {t('profit')} = 1 USDT {t('balance')}</span>
                </div>
                <div className="available-balance">
                  <span>{t('availableProfitBalance')}: <FormattedNumber value={user?.totalProfit || 0} suffix="USDT" /></span>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="profitAmount">{t('profitAmountToConvert')}</label>
                <input
                  id="profitAmount"
                  type="number"
                  placeholder={t('enterProfitAmount')}
                  value={profitAmount}
                  onChange={(e) => {
                    setProfitAmount(e.target.value);
                    setConvertError('');
                  }}
                  min="1"
                  max={user?.totalProfit || 0}
                  step="0.01"
                />
                <div className="minimum-hint">
                  {t('minimumConversion')}: 1 USDT
                </div>
              </div>

              {profitAmount && parseFloat(profitAmount) >= 1 && (
                <div className="conversion-preview">
                  <div className="preview-row">
                    <span>{t('profitToConvert')}: {parseFloat(profitAmount).toFixed(2)} USDT</span>
                  </div>
                  <div className="preview-row">
                    <span>{t('balanceToReceive')}: {parseFloat(profitAmount).toFixed(2)} USDT</span>
                  </div>
                </div>
              )}

              {convertError && (
                <div className="error-message">{convertError}</div>
              )}
            </div>

            <div className="fox-modal-footer">
              <SwipeToGetFoxButton
                onGetFox={handleSwipeProfitConvert}
                disabled={isConverting || !profitAmount || parseFloat(profitAmount) < 1 || parseFloat(profitAmount) > (user?.totalProfit || 0)}
                customText={{
                  default: t('swipeToConvertProfit'),
                  threshold: t('releaseToConvertProfit'),
                  processing: t('converting'),
                  completed: `${t('converted')} ${profitAmount || 0} USDT`
                }}
              />
            </div>
          </div>
        </>
      )}

      {/* Trade Details Modal */}
      {showTradeDetailsModal && selectedTrade && (
        <>
          <div className="backdrop-overlay" onClick={closeTradeDetailsModal}></div>
          <div className="trade-details-modal">
            <div className="trade-details-header">
              <h2>{t('tradeDetails')}</h2>
              <button className="modal-close" onClick={closeTradeDetailsModal}>
                ×
              </button>
            </div>

            <div className="trade-details-body">
              {/* Main trade info */}
              <div className="trade-info-section">
                <div className="trade-info-row">
                  <span className="trade-info-label">{t('tradingPair')}</span>
                  <span className="trade-info-value">{selectedTrade.pair}</span>
                </div>
                <div className="trade-info-row">
                  <span className="trade-info-label">{t('exchanges')}</span>
                  <span className="trade-info-value" style={{
                    display: 'flex',
                    gap: '5px'
                  }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '5px',
                      alignItems: 'center'
                    }}>
                      {selectedTrade.sourceExchange}
                      <span style={{
                        fontSize: '10px',
                        opacity: 0.5
                      }}>{selectedTrade.sourcePrice}</span>
                    </div>
                    →
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '5px',
                      alignItems: 'center'
                    }}>
                      {selectedTrade.targetExchange}
                      <span style={{
                        fontSize: '10px',
                        opacity: 0.5
                      }}>{selectedTrade.targetPrice}</span>
                    </div>
                  </span>
                </div>
                <div className="trade-info-row">
                  <span className="trade-info-label">{t('date')}</span>
                  <span className="trade-info-value">
                    {selectedTrade.date.toLocaleDateString('ru-RU')} {selectedTrade.date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div className="trade-info-row">
                  <span className="trade-info-label">{t('status')}</span>
                  <span className={`trade-status ${selectedTrade.status}`}>
                    {t(selectedTrade.status)}
                  </span>
                </div>
              </div>

              {/* Financial details */}
              <div className="trade-financial-section">
                <h3>{t('financialDetails')}</h3>

                <div className="financial-card">
                  <div className="financial-item">
                    <span className="financial-label">{t('tradeAmount')}</span>
                    <span className="financial-value">
                      <FormattedNumber value={selectedTrade.amount} suffix="USDT" />
                    </span>
                  </div>

                  <div className="financial-item profit-item">
                    <span className="financial-label">{t('profit')}</span>
                    <span className="financial-value profit-value">
                      +<FormattedNumber value={selectedTrade.profit} suffix="USDT" />
                    </span>
                  </div>

                  <div className="financial-item">
                    <span className="financial-label">{t('spread')}</span>
                    <span className="financial-value spread-value">
                      <FormattedNumber value={selectedTrade.profitPercent} suffix="%" />
                    </span>
                  </div>

                  <div className="financial-item">
                    <span className="financial-label">{t('comission')} ({selectedTrade.commissionPercent}%)</span>
                    <span className="financial-value spread-value">
                      <FormattedNumber value={selectedTrade.commissionAmount} suffix="USDT" />
                    </span>
                  </div>
                </div>
              </div>

              {/* Transaction steps */}
              <div className="trade-steps-section">
                <h3>{t('transactionSteps')}</h3>

                <div className="steps-container">
                  <div className="step-item completed">
                    <div className="step-icon">✓</div>
                    <div className="step-content">
                      <div className="step-title">{t('buyOrder')}</div>
                      <div className="step-description">
                        {t('boughtOn')} {selectedTrade.sourceExchange}
                      </div>
                    </div>
                  </div>

                  <div className="step-divider"></div>

                  <div className="step-item completed">
                    <div className="step-icon">✓</div>
                    <div className="step-content">
                      <div className="step-title">{t('sellOrder')}</div>
                      <div className="step-description">
                        {t('soldOn')} {selectedTrade.targetExchange}
                      </div>
                    </div>
                  </div>

                  <div className="step-divider"></div>

                  <div className="step-item completed">
                    <div className="step-icon">✓</div>
                    <div className="step-content">
                      <div className="step-title">{t('profitRealized')}</div>
                      <div className="step-description">
                        +<FormattedNumber value={selectedTrade.profit} suffix="USDT" /> {t('addedToBalance')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Exchange Connection Modal */}
      <ExchangeConnectionModal
        isOpen={showExchangeConnectionModal}
        onClose={() => setShowExchangeConnectionModal(false)}
        onConnect={handleConnectExchange}
      />
    </div>
  );
};

export default Dashboard; 